"use client";

import React, { useState, useRef } from "react";
import { useForm } from "react-hook-form";
import emailjs from "emailjs-com";
import { Element } from "react-scroll";
import { motion, useInView, AnimatePresence } from "framer-motion";
import { FaGithub, FaLinkedin, FaInstagramSquare } from "react-icons/fa";

const fadeUp = {
  hidden: { y: 48, opacity: 0 },
  show: (d = 0) => ({
    y: 0,
    opacity: 1,
    transition: { duration: 0.75, delay: d, ease: [0.22, 1, 0.36, 1] },
  }),
};

const stagger = {
  hidden: {},
  show: { transition: { staggerChildren: 0.1, delayChildren: 0.05 } },
};

const fieldVariant = {
  hidden: { y: 28, opacity: 0 },
  show: {
    y: 0,
    opacity: 1,
    transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] },
  },
};

function Field({ label, glyph, error, children }) {
  return (
    <motion.div variants={fieldVariant} className="flex flex-col gap-2">
      <label
        className="flex items-center gap-2 text-[10px] tracking-[0.25em] uppercase text-white/35"
        style={{ fontFamily: "'DM Mono', monospace" }}
      >
        <span className="text-[#00f0ff]/50 text-xs">{glyph}</span>
        {label}
      </label>
      {children}
      <AnimatePresence>
        {error && (
          <motion.p
            initial={{ opacity: 0, y: -6, height: 0 }}
            animate={{ opacity: 1, y: 0, height: "auto" }}
            exit={{ opacity: 0, y: -6, height: 0 }}
            transition={{ duration: 0.25 }}
            className="text-[10px] tracking-[0.12em] text-red-400/80"
            style={{ fontFamily: "'DM Mono', monospace" }}
          >
            ⚠ {error}
          </motion.p>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

/* styled input classes */
const inputCls = `
  w-full bg-white/[0.03] border border-white/[0.07]
  rounded-xl px-4 py-3.5 text-sm text-white/80 outline-none
  placeholder:text-white/20
  focus:border-[#00f0ff]/40 focus:bg-[#00f0ff]/[0.03]
  hover:border-white/15
  transition-all duration-300
  font-light
`;

/* ─────────────────────────────────────────
   CONTACT INFO PILL
───────────────────────────────────────── */
function InfoPill({ glyph, label, value, href }) {
  const inner = (
    <div className="flex items-start gap-3 group">
      <div
        className="flex-shrink-0 w-9 h-9 rounded-xl flex items-center justify-center
          border border-[#00f0ff]/15 bg-[#00f0ff]/[0.04]
          group-hover:border-[#00f0ff]/35 group-hover:bg-[#00f0ff]/[0.08]
          transition-colors duration-300"
      >
        <span className="text-[#00f0ff]/60 text-sm">{glyph}</span>
      </div>
      <div>
        <div
          className="text-[9px] tracking-[0.24em] text-white/22 uppercase mb-0.5"
          style={{ fontFamily: "'DM Mono', monospace" }}
        >
          {label}
        </div>
        <div
          className="text-sm text-white/55 group-hover:text-[#00f0ff]/80 transition-colors duration-300"
          style={{ fontFamily: "'DM Sans', sans-serif" }}
        >
          {value}
        </div>
      </div>
    </div>
  );

  return href ? (
    <a href={href} target="_blank" rel="noopener noreferrer" className="block">
      {inner}
    </a>
  ) : (
    <div>{inner}</div>
  );
}

/* ─────────────────────────────────────────
   SOCIAL BUTTON
───────────────────────────────────────── */
function SocialBtn({ href, Icon, label }) {
  return (
    <motion.a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={label}
      whileHover={{ scale: 1.12, y: -3 }}
      whileTap={{ scale: 0.9 }}
      className="group w-11 h-11 rounded-full flex items-center justify-center
        border border-white/[0.07] bg-white/[0.025]
        hover:border-[#00f0ff]/40 hover:bg-[#00f0ff]/[0.07]
        transition-colors duration-300"
    >
      <Icon className="w-4 h-4 text-white/40 group-hover:text-[#00f0ff] transition-colors duration-300" />
    </motion.a>
  );
}

/* ─────────────────────────────────────────
   MAIN CONTACT
───────────────────────────────────────── */
const Contact = () => {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm();

  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null); // null | 'success' | 'error'

  const headRef = useRef(null);
  const formRef = useRef(null);
  const headInView = useInView(headRef, { once: true, margin: "-60px" });
  const formInView = useInView(formRef, { once: true, margin: "-80px" });

  const onSubmit = async (data) => {
    setLoading(true);
    setStatus(null);
    try {
      await emailjs.send(
        process.env.NEXT_PUBLIC_EMAIL_SERVICE_KEY,
        process.env.NEXT_PUBLIC_EMAIL_TEMPLATE_KEY,
        {
          to_name: "Abu Bokor",
          from_name: data.name,
          from_email: data.email,
          subject: data.subject,
          message: data.message,
        },
        process.env.NEXT_PUBLIC_EMAIL_PUBLIC_KEY,
      );
      setStatus("success");
      reset();
    } catch (err) {
      console.error(err);
      setStatus("error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Element name="contact">
      <section
        className="relative overflow-hidden"
        style={{ background: "#05050b" }}
      >
        {/* ── Grid bg ── */}
        <svg
          className="absolute inset-0 w-full h-full pointer-events-none opacity-[0.03]"
          aria-hidden
        >
          <defs>
            <pattern
              id="contact-grid"
              width="72"
              height="72"
              patternUnits="userSpaceOnUse"
            >
              <path
                d="M 72 0 L 0 0 0 72"
                fill="none"
                stroke="white"
                strokeWidth="0.4"
              />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#contact-grid)" />
        </svg>

        {/* ── Orbs ── */}
        <div
          className="absolute top-0 left-0 w-[500px] h-[500px] pointer-events-none"
          aria-hidden
        >
          <div
            style={{
              background:
                "radial-gradient(circle, rgba(0,71,255,0.12) 0%, transparent 70%)",
              filter: "blur(90px)",
              width: "100%",
              height: "100%",
            }}
          />
        </div>
        <div
          className="absolute bottom-0 right-0 w-[450px] h-[450px] pointer-events-none"
          aria-hidden
        >
          <div
            style={{
              background:
                "radial-gradient(circle, rgba(0,240,255,0.08) 0%, transparent 70%)",
              filter: "blur(90px)",
              width: "100%",
              height: "100%",
            }}
          />
        </div>

        {/* ── Top border beam ── */}
        <motion.div
          initial={{ scaleX: 0 }}
          animate={headInView ? { scaleX: 1 } : {}}
          transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1] }}
          className="absolute top-0 left-0 right-0 h-px origin-left pointer-events-none"
          style={{
            background:
              "linear-gradient(90deg, transparent, #0047ff, #00f0ff, transparent)",
          }}
        />

        <div className="relative z-10 max-w-7xl mx-auto px-5 md:px-10 pt-20 pb-10">
          {/* ── HEADING ── */}
          <div ref={headRef} className="mb-16 lg:mb-20">
            <motion.div
              custom={0}
              variants={fadeUp}
              initial="hidden"
              animate={headInView ? "show" : "hidden"}
              className="flex items-center gap-4 mb-5"
            >
              <div className="h-px w-12 bg-gradient-to-r from-[#0047ff] to-[#00f0ff]" />
              <span
                className="text-[10px] tracking-[0.32em] text-[#00f0ff]/60 uppercase"
                style={{ fontFamily: "'DM Mono', monospace" }}
              >
                06 — Contact
              </span>
            </motion.div>

            <div className="overflow-hidden">
              <motion.h2
                custom={0.1}
                variants={fadeUp}
                initial="hidden"
                animate={headInView ? "show" : "hidden"}
                className="text-[clamp(3rem,9vw,7.5rem)] font-black leading-[0.88] tracking-tight text-white"
                style={{ fontFamily: "'Syne', sans-serif" }}
              >
                LET'S
              </motion.h2>
            </div>
            <div className="overflow-hidden">
              <motion.h2
                custom={0.2}
                variants={fadeUp}
                initial="hidden"
                animate={headInView ? "show" : "hidden"}
                className="text-[clamp(3rem,9vw,7.5rem)] font-black leading-[0.88] tracking-tight"
                style={{
                  fontFamily: "'Syne', sans-serif",
                  WebkitTextStroke: "1.5px rgba(0,240,255,0.32)",
                  color: "transparent",
                }}
              >
                BUILD.
              </motion.h2>
            </div>

            <motion.div
              initial={{ scaleX: 0 }}
              animate={headInView ? { scaleX: 1 } : {}}
              transition={{
                duration: 1,
                delay: 0.35,
                ease: [0.22, 1, 0.36, 1],
              }}
              className="mt-5 h-px w-40 origin-left"
              style={{
                background:
                  "linear-gradient(90deg, #0047ff, #00f0ff60, transparent)",
              }}
            />

            <motion.p
              custom={0.38}
              variants={fadeUp}
              initial="hidden"
              animate={headInView ? "show" : "hidden"}
              className="mt-5 text-sm text-white/30 max-w-sm leading-relaxed"
              style={{ fontFamily: "'DM Sans', sans-serif" }}
            >
              Have a project, an idea, or just want to talk engineering? I'm
              listening.
            </motion.p>
          </div>

          {/* ── TWO-COLUMN LAYOUT ── */}
          <div
            ref={formRef}
            className="grid grid-cols-1 lg:grid-cols-5 gap-10 lg:gap-16 items-start"
          >
            {/* ── LEFT — Info panel (2/5) ── */}
            <motion.div
              initial="hidden"
              animate={formInView ? "show" : "hidden"}
              variants={stagger}
              className="lg:col-span-2 flex flex-col gap-8"
            >
              {/* Status indicator */}
              <motion.div
                variants={fieldVariant}
                className="inline-flex items-center gap-2.5 px-4 py-2.5 rounded-full
                  border border-[#00f0ff]/15 bg-[#00f0ff]/[0.04] self-start"
              >
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#00f0ff] opacity-60" />
                  <span className="relative inline-flex h-2 w-2 rounded-full bg-[#00f0ff]" />
                </span>
                <span
                  className="text-[10px] tracking-[0.22em] uppercase text-[#00f0ff]/70"
                  style={{ fontFamily: "'DM Mono', monospace" }}
                >
                  Available for work
                </span>
              </motion.div>

              {/* Contact info */}
              <motion.div
                variants={fieldVariant}
                className="flex flex-col gap-5"
              >
                <InfoPill
                  glyph="◈"
                  label="Email"
                  value="abubokor1066@gmail.com"
                  href="mailto:abubokor1066@gmail.com"
                />
                <InfoPill
                  glyph="◉"
                  label="Phone"
                  value="+880 1885 236058"
                  href="tel:+8801885236058"
                />
                <InfoPill glyph="◎" label="Location" value="Bangladesh" />
                <InfoPill
                  glyph="◐"
                  label="Response time"
                  value="Within 24 hours"
                />
              </motion.div>

              {/* Divider */}
              <motion.div
                variants={fieldVariant}
                className="h-px w-full"
                style={{
                  background:
                    "linear-gradient(90deg, #0047ff30, #00f0ff15, transparent)",
                }}
              />

              {/* What I'm open to */}
              <motion.div
                variants={fieldVariant}
                className="flex flex-col gap-3"
              >
                <span
                  className="text-[9px] tracking-[0.28em] uppercase text-white/22"
                  style={{ fontFamily: "'DM Mono', monospace" }}
                >
                  Open to
                </span>
                {[
                  "Full-time positions",
                  "Freelance projects",
                  "Technical consulting",
                  "Open-source collaboration",
                ].map((item) => (
                  <div key={item} className="flex items-center gap-3">
                    <span className="w-1 h-1 rounded-full bg-[#00f0ff]/50 flex-shrink-0" />
                    <span
                      className="text-sm text-white/40"
                      style={{ fontFamily: "'DM Sans', sans-serif" }}
                    >
                      {item}
                    </span>
                  </div>
                ))}
              </motion.div>

              {/* Divider */}
              <motion.div
                variants={fieldVariant}
                className="h-px w-full"
                style={{
                  background: "linear-gradient(90deg, #0047ff30, transparent)",
                }}
              />

              {/* Socials */}
              <motion.div
                variants={fieldVariant}
                className="flex flex-col gap-3"
              >
                <span
                  className="text-[9px] tracking-[0.28em] uppercase text-white/22"
                  style={{ fontFamily: "'DM Mono', monospace" }}
                >
                  Find me on
                </span>
                <div className="flex items-center gap-3">
                  <SocialBtn
                    href="https://github.com/AbuBokorprog"
                    Icon={FaGithub}
                    label="GitHub"
                  />
                  <SocialBtn
                    href="https://www.linkedin.com/in/md-abu-bokor/"
                    Icon={FaLinkedin}
                    label="LinkedIn"
                  />
                  <SocialBtn
                    href="#"
                    Icon={FaInstagramSquare}
                    label="Instagram"
                  />
                </div>
              </motion.div>
            </motion.div>

            {/* ── RIGHT — Form (3/5) ── */}
            <motion.div
              initial="hidden"
              animate={formInView ? "show" : "hidden"}
              variants={stagger}
              className="lg:col-span-3"
            >
              {/* Form card */}
              <div className="relative overflow-hidden rounded-2xl border border-white/[0.06] bg-white/[0.025]">
                {/* Inner top glow */}
                <div
                  className="absolute top-0 left-0 right-0 h-px pointer-events-none"
                  style={{
                    background:
                      "linear-gradient(90deg, transparent, #0047ff50, #00f0ff30, transparent)",
                  }}
                />
                {/* Corner brackets */}
                <svg
                  className="absolute top-4 left-4 opacity-20"
                  width="14"
                  height="14"
                  fill="none"
                >
                  <path
                    d="M0 14 L0 0 L14 0"
                    stroke="#00f0ff"
                    strokeWidth="1.2"
                  />
                </svg>
                <svg
                  className="absolute bottom-4 right-4 opacity-20"
                  width="14"
                  height="14"
                  fill="none"
                >
                  <path
                    d="M14 0 L14 14 L0 14"
                    stroke="#0047ff"
                    strokeWidth="1.2"
                  />
                </svg>

                <form
                  onSubmit={handleSubmit(onSubmit)}
                  className="p-7 md:p-10 flex flex-col gap-6"
                >
                  {/* Name + Email row */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <Field
                      label="Your Name"
                      glyph="◈"
                      error={errors.name?.message}
                    >
                      <input
                        placeholder="John Doe"
                        className={inputCls}
                        style={{ fontFamily: "'DM Sans', sans-serif" }}
                        {...register("name", {
                          required: "Name is required",
                          maxLength: 50,
                        })}
                      />
                    </Field>
                    <Field
                      label="Email Address"
                      glyph="◉"
                      error={errors.email?.message}
                    >
                      <input
                        type="email"
                        placeholder="john@example.com"
                        className={inputCls}
                        style={{ fontFamily: "'DM Sans', sans-serif" }}
                        {...register("email", {
                          required: "Email is required",
                          pattern: {
                            value: /^\S+@\S+$/i,
                            message: "Invalid email",
                          },
                        })}
                      />
                    </Field>
                  </div>

                  {/* Subject */}
                  <Field
                    label="Subject"
                    glyph="◎"
                    error={errors.subject?.message}
                  >
                    <input
                      placeholder="What's this about?"
                      className={inputCls}
                      style={{ fontFamily: "'DM Sans', sans-serif" }}
                      {...register("subject", {
                        required: "Subject is required",
                        maxLength: 100,
                      })}
                    />
                  </Field>

                  {/* Message */}
                  <Field
                    label="Message"
                    glyph="◐"
                    error={errors.message?.message}
                  >
                    <textarea
                      rows={6}
                      placeholder="Tell me about your project, idea, or anything else..."
                      className={`${inputCls} resize-none`}
                      style={{ fontFamily: "'DM Sans', sans-serif" }}
                      {...register("message", {
                        required: "Message is required",
                        minLength: {
                          value: 10,
                          message: "At least 10 characters",
                        },
                        maxLength: 1000,
                      })}
                    />
                  </Field>

                  {/* Status banners */}
                  <AnimatePresence>
                    {status === "success" && (
                      <motion.div
                        key="success"
                        initial={{ opacity: 0, y: 10, scale: 0.97 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: -10, scale: 0.97 }}
                        transition={{ duration: 0.4 }}
                        className="flex items-center gap-3 px-5 py-4 rounded-xl
                          border border-[#00f0ff]/25 bg-[#00f0ff]/[0.05]"
                      >
                        <span className="text-[#00f0ff] text-lg flex-shrink-0">
                          ✓
                        </span>
                        <p
                          className="text-sm text-[#00f0ff]/80"
                          style={{ fontFamily: "'DM Sans', sans-serif" }}
                        >
                          Message transmitted successfully. I'll get back to you
                          within 24 hours.
                        </p>
                      </motion.div>
                    )}
                    {status === "error" && (
                      <motion.div
                        key="error"
                        initial={{ opacity: 0, y: 10, scale: 0.97 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: -10, scale: 0.97 }}
                        transition={{ duration: 0.4 }}
                        className="flex items-center gap-3 px-5 py-4 rounded-xl
                          border border-red-500/25 bg-red-500/[0.05]"
                      >
                        <span className="text-red-400 text-lg flex-shrink-0">
                          ⚠
                        </span>
                        <p
                          className="text-sm text-red-400/80"
                          style={{ fontFamily: "'DM Sans', sans-serif" }}
                        >
                          Transmission failed. Please try again or email me
                          directly.
                        </p>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  {/* Submit */}
                  <motion.button
                    type="submit"
                    disabled={loading}
                    whileHover={!loading ? { scale: 1.02 } : {}}
                    whileTap={!loading ? { scale: 0.97 } : {}}
                    className="relative group w-full flex items-center justify-center gap-3
                      py-4 rounded-xl font-semibold text-sm tracking-[0.2em] uppercase
                      text-[#05050b] overflow-hidden
                      disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{
                      background: loading
                        ? "#1a1a2e"
                        : "linear-gradient(135deg, #00f0ff 0%, #0047ff 100%)",
                      boxShadow: loading
                        ? "none"
                        : "0 0 35px rgba(0,240,255,0.22)",
                      fontFamily: "'DM Mono', monospace",
                      color: loading ? "rgba(255,255,255,0.4)" : "#05050b",
                      border: loading
                        ? "1px solid rgba(255,255,255,0.08)"
                        : "none",
                    }}
                  >
                    {/* Shimmer */}
                    {!loading && (
                      <span
                        className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                        style={{
                          background:
                            "linear-gradient(135deg, rgba(255,255,255,0.2), transparent 60%)",
                        }}
                      />
                    )}

                    {loading ? (
                      <>
                        <motion.span
                          animate={{ rotate: 360 }}
                          transition={{
                            duration: 1,
                            repeat: Infinity,
                            ease: "linear",
                          }}
                          className="w-4 h-4 border-2 border-white/30 border-t-white/60 rounded-full flex-shrink-0"
                        />
                        <span className="relative z-10 text-white/40">
                          Transmitting…
                        </span>
                      </>
                    ) : (
                      <>
                        <span className="relative z-10">Send Message</span>
                        <motion.span
                          className="relative z-10"
                          animate={{ x: [0, 4, 0] }}
                          transition={{ duration: 1.5, repeat: Infinity }}
                        >
                          →
                        </motion.span>
                      </>
                    )}
                  </motion.button>

                  {/* Direct email fallback */}
                  <p
                    className="text-center text-[10px] tracking-[0.15em] text-white/20"
                    style={{ fontFamily: "'DM Mono', monospace" }}
                  >
                    or reach out at{" "}
                    <a
                      href="mailto:abubokor1066@gmail.com"
                      className="text-[#00f0ff]/40 hover:text-[#00f0ff]/70 transition-colors duration-300 underline underline-offset-2"
                    >
                      abubokor1066@gmail.com
                    </a>
                  </p>
                </form>
              </div>
            </motion.div>
          </div>

          {/* ── FOOTER STAMP ── */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={formInView ? { opacity: 1 } : {}}
            transition={{ delay: 1.2, duration: 0.8 }}
            className="mt-20 flex items-center gap-4"
          >
            <div
              className="h-px flex-1 max-w-7xl"
              style={{
                background:
                  "linear-gradient(90deg, transparent, #0047ff30, transparent)",
              }}
            />
            <span
              className="text-[9px] tracking-[0.28em] text-white/16 uppercase"
              style={{ fontFamily: "'DM Mono', monospace" }}
            >
              Abu Bokor — {new Date().getFullYear()}
            </span>
            <div
              className="h-px flex-1 max-w-7xl"
              style={{
                background:
                  "linear-gradient(90deg, transparent, #00f0ff20, transparent)",
              }}
            />
          </motion.div>
        </div>

        {/* ── Bottom border beam ── */}
        <motion.div
          initial={{ scaleX: 0, opacity: 0 }}
          animate={formInView ? { scaleX: 1, opacity: 1 } : {}}
          transition={{ duration: 1.4, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="absolute bottom-0 left-0 right-0 h-px origin-right pointer-events-none"
          style={{
            background:
              "linear-gradient(90deg, transparent, #0047ff40, #00f0ff30, transparent)",
          }}
        />
      </section>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Syne:wght@700;800;900&family=DM+Mono:ital,wght@0,300;0,400;0,500&family=DM+Sans:wght@300;400;500&display=swap');
      `}</style>
    </Element>
  );
};

export default Contact;
